<template>
  <section class="cfo-panel forecast-panel" :class="scope.clsPrefix">
    <div class="panel-head">
      <div class="panel-title">T+3 滚动预测与情景模拟</div>
      <span class="tag info">T+1周更新 · AI 驱动</span>
    </div>
    <div v-if="loading" class="panel-body loading-state">数据加载中...</div>
    <div v-else-if="error" class="panel-body error-state">数据加载失败，请稍后重试</div>
    <template v-else>
    <div class="panel-body">
      <div class="charts-row">
        <div class="chart-panel">
          <div class="chart-header">
            <div>
              <div class="chart-title">毛利率走势与 AI 预测</div>
              <div class="chart-sub">实际值 + 预测区间 (80% CI) + 目标线</div>
            </div>
            <div class="chart-meta">
              <div class="meta-val">R² = {{ forecastRsq }}</div>
              <div class="meta-sub">MAPE {{ forecastMape }}</div>
            </div>
          </div>
          <div :id="lineChartId" class="chart-box"></div>
        </div>
        <div class="chart-panel">
          <div class="chart-header sens-header">
            <div class="chart-title">毛利率敏感度分析 <span class="chart-sub inline">|ε| 弹性系数</span></div>
            <div class="sens-legend">
              <span class="sens-good">利好</span>
              <span class="sens-bad">利空</span>
            </div>
          </div>
          <div :id="barChartId" class="chart-box sens-chart-box"></div>
        </div>
      </div>
      <div class="scenario-table">
        <div class="table-scroll">
          <table class="scenario-gap-table">
            <thead>
              <tr>
                <th>情景</th>
                <th>概率</th>
                <th class="num">6月毛利率</th>
                <th class="num">利润缺口</th>
                <th class="num">采购兑现</th>
                <th class="num">锁价进展</th>
                <th>关键假设</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in scenarios" :key="index">
                <td><span :class="['scenario-name', row.tagClass]">{{ row.name }}</span></td>
                <td class="num">{{ row.probability }}</td>
                <td class="num">{{ row.margin }}</td>
                <td class="num"><span :class="row.gapClass">{{ row.gap }}</span></td>
                <td class="num"><span :class="row.fulfillClass">{{ row.fulfill }}</span></td>
                <td class="num">{{ row.lockPrice }}</td>
                <td class="assumption" :title="row.assumption">{{ row.assumption }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </template>
  </section>
</template>

<script>
import * as echarts from 'echarts'
import { fetchScenarioSimulation, unwrapDashboardResponse } from '@/api/dashboard'

/**
 * 敏感度物料代码 → 中文显示名映射
 */
const SENS_LABEL_MAP = {
  MAT_STEEL: '钢材成本上涨',
  MAT_COPPER: '铜材料涨价',
  MAT_PCB: 'PCB材料涨价',
  MAT_OTHER: '其他材料上涨'
}

/**
 * 敏感度标签映射（当无中文映射时的备选）
 */

export default {
  name: 'CfoForecastSimulation',
  data() {
    const uid = this._uid
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cfo-forecast'
      },
      lineChartId: 'cfo_line_chart_' + uid,
      barChartId: 'cfo_bar_chart_' + uid,
      lineChart: null,
      barChart: null,
      scenarios: [],
      forecastRsq: '0.89',
      forecastMape: '2.1%',
      loading: false,
      error: false
    }
  },
  mounted() {
    this.loadData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.lineChart) {
      this.lineChart.dispose()
    }
    if (this.barChart) {
      this.barChart.dispose()
    }
  },
  methods: {
    /**
     * 加载情景模拟数据
     */
    async loadData() {
      this.loading = true
      this.error = false
      try {
        const res = await fetchScenarioSimulation()
        const data = unwrapDashboardResponse(res)
        this.buildScenarios(data.scenarios || [])
        this.loading = false
        await this.$nextTick()
        this.drawLineChart(data.trend_points || [])
        this.drawBarChart(data.sensitivity_bars || [])
        window.addEventListener('resize', this.handleResize)
      } catch (e) {
        console.error('获取情景模拟数据失败:', e)
        this.error = true
        this.loading = false
      }
    },
    /**
     * 构建情景表格数据
     * @param {Array} scenarios API返回的情景数组
     */
    buildScenarios(scenarios) {
      this.scenarios = scenarios.map((s) => {
        // 根据情景名称确定样式
        const nameStyle = this.getScenarioStyle(s.scenario_name)
        // 从描述中提取兑现率和锁价信息
        const { fulfill, lockPrice } = this.parseDescription(s.description || '')

        return {
          name: s.scenario_name,
          tagClass: nameStyle.tag,
          probability: (s.probability * 100).toFixed(0) + '%',
          margin: s.june_gm.toFixed(1) + '%',
          gap: (s.profit_gap >= 0 ? '+' : '') + s.profit_gap + '万',
          gapClass: nameStyle.gapClass,
          fulfill,
          fulfillClass: nameStyle.fulfillClass,
          lockPrice,
          assumption: s.description
        }
      })
    },
    /**
     * 获取情景对应的样式配置
     * @param {string} name 情景名称
     * @returns {Object} 样式配置
     */
    getScenarioStyle(name) {
      const map = {
        保守: {
          tag: 'danger',
          gapClass: 'val-red',
          fulfillClass: 'val-red'
        },
        基准: {
          tag: 'info',
          gapClass: 'val-blue',
          fulfillClass: 'val-blue'
        },
        进取: {
          tag: 'success',
          gapClass: 'val-green',
          fulfillClass: 'val-green'
        }
      }
      return map[name] || {
        tag: 'info',
        gapClass: 'val-blue',
        fulfillClass: 'val-blue'
      }
    },
    /**
     * 从描述文本中提取兑现率和锁价信息
     * @param {string} desc 情景描述
     * @returns {{ fulfill: string, lockPrice: string }}
     */
    parseDescription(desc) {
      // 提取兑现率，如 "兑现率30%" → "30%"
      const fulfillMatch = desc.match(/兑现率(\d+)%/)
      const fulfill = fulfillMatch ? fulfillMatch[1] + '%' : '-'

      // 提取锁价/签约信息
      const lockMatch = desc.match(/(\d+)家/)
      const hasSign = desc.includes('签约') || desc.includes('锁价')
      const lockPrice = lockMatch ? lockMatch[1] + '家签约' : (hasSign ? '洽谈中' : '-')

      return { fulfill, lockPrice }
    },
    /**
     * 绘制毛利率走势折线图
     * @param {Array} trendPoints 趋势数据点
     */
    drawLineChart(trendPoints) {
      const dom = document.getElementById(this.lineChartId)
      if (!dom || !trendPoints.length) return

      const sorted = [...trendPoints].sort((a, b) => a.month_label.localeCompare(b.month_label))
      const xLabels = sorted.map(p => {
        const parts = p.month_label.split('年')
        return parts.length > 1 ? parts[1] : p.month_label
      })

      const actualData = sorted.map(p => (p.type === 'actual' ? p.gm_pct : null))
      const forecastData = sorted.map(p => (p.type !== 'actual' ? p.gm_pct : null))

      let lastActualIdx = -1
      let firstForecastIdx = -1
      sorted.forEach((p, i) => {
        if (p.type === 'actual') lastActualIdx = i
        if (p.type !== 'actual' && firstForecastIdx === -1) firstForecastIdx = i
      })
      // 在最后一个实际点补桥接，使预测线从 7 月连续到 8 月
      if (lastActualIdx >= 0 && firstForecastIdx > lastActualIdx) {
        forecastData[lastActualIdx] = sorted[lastActualIdx].gm_pct
      }

      const bridgeIdx = lastActualIdx
      const allGm = sorted.map(p => p.gm_pct).filter(v => v != null)
      const yMin = Math.floor(Math.min(...allGm, 16) - 1)
      const yMax = Math.ceil(Math.max(...allGm, 16) + 1)

      if (this.lineChart) {
        this.lineChart.dispose()
      }
      this.lineChart = echarts.init(dom)
      this.lineChart.setOption({
        grid: {
          left: 42,
          right: 52,
          top: 28,
          bottom: 36
        },
        legend: {
          data: ['实际', '预测'],
          bottom: 0,
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 9, color: '#8c9bab' }
        },
        tooltip: {
          trigger: 'axis',
          confine: true,
          textStyle: { fontSize: 11 },
          formatter(params) {
            const items = params.filter(p => p.value != null)
            if (!items.length) return ''
            let html = `${items[0].axisValue}<br/>`
            items.forEach(item => {
              html += `${item.marker}${item.seriesName}: <b>${Number(item.value).toFixed(2)}%</b><br/>`
            })
            return html
          }
        },
        xAxis: {
          type: 'category',
          data: xLabels,
          axisLabel: {
            fontSize: 10,
            color: '#8c9bab',
            formatter: (val, idx) => {
              const point = sorted[idx]
              if (point && point.type !== 'actual') {
                return `{fc|${val}}`
              }
              return val
            },
            rich: {
              fc: { color: '#4A5FF3', fontWeight: 600 }
            }
          },
          axisLine: {
            lineStyle: { color: '#edf1f5' }
          }
        },
        yAxis: {
          type: 'value',
          min: yMin,
          max: yMax,
          axisLabel: {
            fontSize: 10,
            color: '#8c9bab',
            formatter: '{value}%'
          },
          splitLine: {
            lineStyle: { color: '#f1f5f9' }
          }
        },
        series: [
          {
            name: '实际',
            type: 'line',
            data: actualData,
            smooth: true,
            connectNulls: false,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: { color: '#E54B3D', width: 2 },
            itemStyle: { color: '#E54B3D', borderColor: '#fff', borderWidth: 2 },
            areaStyle: { color: 'rgba(229,75,61,0.08)' },
            label: {
              show: true,
              position: 'top',
              distance: 6,
              fontSize: 9,
              fontWeight: 700,
              color: '#E54B3D',
              formatter: (p) => (p.value != null ? Number(p.value).toFixed(1) + '%' : '')
            },
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: { color: '#E54B3D', type: 'dashed', opacity: 0.45 },
              data: [{
                yAxis: 16.0,
                label: {
                  formatter: '目标 16.0%',
                  fontSize: 9,
                  color: '#E54B3D',
                  position: 'insideEndTop'
                }
              }]
            }
          },
          {
            name: '预测',
            type: 'line',
            data: forecastData,
            smooth: true,
            connectNulls: true,
            symbol: 'circle',
            symbolSize: (value, params) => (params.dataIndex === bridgeIdx ? 0 : 6),
            lineStyle: { color: '#4A5FF3', width: 2, type: 'dashed' },
            itemStyle: { color: '#4A5FF3', borderColor: '#fff', borderWidth: 2 },
            label: {
              show: true,
              position: 'top',
              distance: 6,
              fontSize: 9,
              fontWeight: 700,
              color: '#4A5FF3',
              formatter: (p) => {
                if (p.dataIndex === bridgeIdx) return ''
                return p.value != null ? Number(p.value).toFixed(1) + '%' : ''
              }
            }
          }
        ]
      })
    },
    /**
     * 绘制敏感度分析条形图（龙卷风图风格）
     * @param {Array} sensitivityBars 敏感度数据
     */
    drawBarChart(sensitivityBars) {
      const dom = document.getElementById(this.barChartId)
      if (!dom || !sensitivityBars.length) return

      const sorted = [...sensitivityBars].sort((a, b) => Math.abs(a.value) - Math.abs(b.value))
      const names = sorted.map(b => SENS_LABEL_MAP[b.label] || b.label)
      const values = sorted.map(b => (b.direction === 'left' ? -Math.abs(b.value) : Math.abs(b.value)))
      const maxAbsVal = Math.max(...values.map(v => Math.abs(v)), 0.1)
      // 与左侧走势图同高，避免因动态加高导致条间距过大
      const chartHeight = 200
      dom.style.height = chartHeight + 'px'

      if (this.barChart) {
        this.barChart.dispose()
      }
      this.barChart = echarts.init(dom)
      this.barChart.setOption({
        grid: {
          left: 4,
          right: 12,
          top: 4,
          bottom: 4,
          containLabel: true
        },
        tooltip: {
          trigger: 'item',
          confine: true,
          textStyle: { fontSize: 11 },
          formatter(params) {
            const v = params.value
            const isBad = v < 0
            const sign = isBad ? '−' : '+'
            const absVal = Math.abs(v)
            return `${params.name}<br/>影响: <b>${sign}${absVal.toFixed(2)} pct</b><br/>|ε| = ${absVal.toFixed(2)}`
          }
        },
        xAxis: {
          type: 'value',
          min: -maxAbsVal * 1.35,
          max: maxAbsVal * 1.35,
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false }
        },
        yAxis: {
          type: 'category',
          data: names,
          inverse: true,
          axisLabel: {
            fontSize: 11,
            color: '#333333',
            fontWeight: 400,
            margin: 8,
            width: 108,
            overflow: 'truncate',
            align: 'right'
          },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        series: [{
          type: 'bar',
          data: values.map((v) => {
            const isBad = v < 0
            const color = isBad ? '#E86452' : '#2DB680'
            const absVal = Math.abs(v)
            const sign = isBad ? '−' : '+'
            const labelText = `${sign}${absVal.toFixed(2)} pct  |ε|=${absVal.toFixed(2)}`
            const inside = absVal >= maxAbsVal * 0.28
            return {
              value: v,
              itemStyle: {
                color,
                borderRadius: 0
              },
              label: {
                show: true,
                position: inside ? 'inside' : (isBad ? 'left' : 'right'),
                distance: 4,
                fontSize: 10,
                color: inside ? '#fff' : color,
                fontWeight: 600,
                formatter: labelText
              }
            }
          }),
          barWidth: 14,
          barCategoryGap: '28%',
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: '#D0D5DD', width: 1, type: 'dashed' },
            label: { show: false },
            data: [{ xAxis: 0 }]
          }
        }]
      })
    },
    /**
     * 窗口大小变化时重绘
     */
    handleResize() {
      if (this.lineChart) {
        this.lineChart.resize()
      }
      if (this.barChart) {
        this.barChart.resize()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cfo-forecast {
  margin-bottom: 0;
  min-height: 0;
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
  box-sizing: border-box;

  .panel-head {
    padding: 0;
    margin-bottom: 10px;
    border-bottom: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 20px;
  }
  .panel-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e2226;
    line-height: 20px;
  }
  .tag.info {
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 4px;
    background: #eff6ff;
    color: #4A5FF3;
    font-weight: 600;
  }
  .panel-body {
    padding: 0;
  }
  .charts-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;
  }
  .chart-panel {
    background: linear-gradient(180deg, #f8fafc, #fff);
    border: 1px solid #edf1f5;
    border-radius: 10px;
    padding: 14px 14px 10px;
    min-height: 240px;
  }
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
  }
  .sens-header {
    align-items: center;
    margin-bottom: 8px;
  }
  .sens-legend {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
  }
  .sens-good {
    color: #2db680;
  }
  .sens-bad {
    color: #e86452;
  }
  .chart-title {
    font-size: 11px;
    font-weight: 700;
    color: #1f2937;
  }
  .chart-sub {
    font-size: 9px;
    color: #8c9bab;
    margin-top: 1px;
    font-weight: 400;
    &.inline {
      display: inline;
      margin-left: 4px;
    }
  }
  .chart-meta {
    text-align: right;
  }
  .meta-val {
    font-size: 9px;
    font-weight: 700;
    color: #4A5FF3;
  }
  .meta-sub {
    font-size: 8px;
    color: #8c9bab;
  }
  .chart-box {
    width: 100%;
    height: 200px;
  }
  .sens-chart-box {
    height: 200px;
  }
  .table-scroll {
    overflow-x: auto;
    scrollbar-width: thin;
  }
  .scenario-gap-table {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;

    th,
    td {
      padding: 10px 12px;
      border: none;
      border-bottom: 1px dashed #e2e8f0;
      text-align: left;
      vertical-align: middle;
      font-size: 12px;
      line-height: 1.45;
      color: #333333;
    }

    th {
      font-size: 11px;
      color: #94a3b8;
      font-weight: 600;
      background: #fff;
      border-bottom: 1px dashed #d9dfec;
    }

    tbody tr:nth-child(odd) td {
      background: #f0f4ff;
    }

    tbody tr:nth-child(even) td {
      background: #fff;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    .num {
      text-align: center;
      font-variant-numeric: tabular-nums;
    }

    .assumption {
      color: #64748b;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 220px;
    }
  }

  .scenario-name {
    font-size: 12px;
    font-weight: 700;
    background: transparent;
    padding: 0;

    &.danger {
      color: #e54b3d;
    }

    &.info {
      color: #4a5ff3;
    }

    &.success {
      color: #0aa66a;
    }
  }

  .val-red {
    color: #e54b3d;
    font-weight: 700;
  }

  .val-blue {
    color: #4a5ff3;
    font-weight: 700;
  }

  .val-green {
    color: #0aa66a;
    font-weight: 700;
  }
  .loading-state,
  .error-state {
    padding: 40px;
    text-align: center;
    font-size: 12px;
    color: #94a3b8;
  }
  .error-state {
    color: #dc2626;
  }
}
</style>
